import type { RefObject } from 'react'
import { Check } from 'lucide-react'
import { AnchoredPopover } from '@/components/ui/AnchoredPopover'

/** Painel de escolha única com ponto colorido (status, prioridade do
 * chamado). Escolher a opção atual só fecha. Teclado: abre com o foco na
 * opção atual, setas ↑/↓ navegam, Enter escolhe, Esc fecha. */
export function DotOptionPicker<T extends string>({
  options,
  current,
  anchorRef,
  onPick,
  onClose,
}: {
  options: ReadonlyArray<{ value: T; label: string; dotClass: string }>
  current: T
  anchorRef: RefObject<HTMLElement | null>
  onPick: (value: T) => void
  onClose: () => void
}) {
  return (
    <AnchoredPopover anchorRef={anchorRef} onClose={onClose} width={240}>
      <ul
        className="p-1"
        role="listbox"
        onKeyDown={(e) => {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
          e.preventDefault()
          const buttons = [...e.currentTarget.querySelectorAll('button')]
          const i = buttons.indexOf(document.activeElement as HTMLButtonElement)
          const next = e.key === 'ArrowDown' ? i + 1 : i - 1
          buttons[(next + buttons.length) % buttons.length]?.focus()
        }}
      >
        {options.map((o) => (
          <li key={o.value} role="option" aria-selected={o.value === current}>
            <button
              type="button"
              autoFocus={o.value === current}
              onClick={() => {
                onClose()
                if (o.value !== current) onPick(o.value)
              }}
              className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] text-body transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${
                o.value === current ? 'bg-blue-50 dark:bg-blue-900/25' : 'hover:bg-surface-muted'
              }`}
            >
              <span className={`h-2 w-2 shrink-0 rounded-full ${o.dotClass}`} aria-hidden />
              <span className="flex-1">{o.label}</span>
              {o.value === current && <Check className="h-3.5 w-3.5 text-blue-600" />}
            </button>
          </li>
        ))}
      </ul>
    </AnchoredPopover>
  )
}
